import { Dialog, DialogTrigger, Modal, Popover } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import PageHeader from '~/components/PageHeader';
import Button from '~/components/ui/Button';
import { CardBox } from '~/components/ui/Card';
import { dialogOverlayClassName, dialogPanelClassName } from '~/components/ui/dialogStyles';
import Icon from '~/components/ui/Icon';
import { SkeletonCard } from '~/components/ui/Skeleton';
import Stack from '~/components/ui/Stack';
import Switch from '~/components/ui/Switch';
import useCompactLayout from '~/lib/useCompactLayout';
import useCurrentUser from '~/modules/user/useCurrentUser';
import useHeroPointSwitch from '~/modules/user/useHeroPointSwitch';
import useZealy from '~/modules/user/useZealy';
import heroLogo from './img/hero.svg';
import zealyLogo from './img/zealy.svg';

const optionsButtonClassName =
  'size-9 shrink-0 border border-options-button-border bg-icon-background text-gray-3 hover:bg-options-button-hover hover:text-foreground';

function Earning() {
  const { t } = useTranslation();
  const { data, isPending } = useZealy();
  const heroPointSwitch = useHeroPointSwitch();
  const user = useCurrentUser();
  const compact = useCompactLayout();
  const RewardsOverlay = compact ? Modal : Popover;

  return (
    <Stack col gap={4} className="compact:gap-5 compact:[&>h2]:text-[13px] compact:[&>h2]:mb-2.5">
      <PageHeader
        id="earning"
        rightElement={
          <DialogTrigger>
            <Button
              variant="icon"
              size="min"
              className={optionsButtonClassName}
              aria-label={t('earning.popover.title')}
            >
              <Icon name="edit" className="text-[20px]" />
            </Button>
            <RewardsOverlay
              {...(compact
                ? { isDismissable: true }
                : { offset: 10, placement: 'bottom end' as const })}
              className={
                compact
                  ? dialogOverlayClassName
                  : 'w-85 max-w-[calc(100vw-2rem)] rounded-2xl border border-popover-border bg-popover-background p-6 text-foreground shadow-popover'
              }
            >
              <Dialog
                className={
                  compact
                    ? `${dialogPanelClassName} px-5 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]`
                    : 'outline-none'
                }
                aria-label={t('earning.popover.title')}
              >
                {({ close }) => (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-sans text-[14px] font-semibold compact:text-lg">
                        {t('earning.popover.title')}
                      </h3>
                      <Button
                        variant="icon"
                        size="min"
                        className={optionsButtonClassName}
                        aria-label="Close"
                        onPress={close}
                      >
                        <Icon name="close" className="text-[20px]" />
                      </Button>
                    </div>
                    <p className="mt-4 text-[12px] leading-[1.8] text-body compact:text-sm compact:leading-[1.6]">
                      {t('earning.popover.note')}
                    </p>
                    <Switch
                      className="mt-6 flex w-full gap-4"
                      label={t('earning.popover.title')}
                      isSelected={user?.hero_points === 'new'}
                      onChange={() =>
                        heroPointSwitch.mutate(user?.hero_points === 'new' ? 'default' : 'new')
                      }
                    />
                  </>
                )}
              </Dialog>
            </RewardsOverlay>
          </DialogTrigger>
        }
      />

      <h2 className="mt-4 font-sans text-[14px] font-semibold text-section-title">
        {t('earning.zealy.title')}
      </h2>
      <CardBox className="flex flex-col p-6 gap-2 min-h-25 compact:p-4 compact:gap-3 compact:min-h-0">
        {isPending && <SkeletonCard height={compact ? 44 : 50} />}

        {data?.level !== undefined && (
          <div>
            <div className="text-[15px] leading-[1.8]">
              <strong>{t('earning.zealy.level')}:</strong> {data?.level || t('earning.zealy.na')}
            </div>
            <div className="text-[15px] leading-[1.8]">
              <strong>{t('earning.zealy.xp')}:</strong> {data?.xp || t('earning.zealy.na')}
            </div>
            <div className="text-[15px] leading-[1.8]">
              <strong>{t('earning.zealy.rank')}:</strong> {data?.rank || t('earning.zealy.na')}
            </div>
          </div>
        )}

        {data === null && (
          <div className="flex flex-wrap items-center gap-6 max-wide:gap-5 compact:gap-3">
            <div className="flex size-13 shrink-0 items-center justify-center rounded-full bg-icon-background compact:size-10">
              <img src={zealyLogo} alt="Zealy Logo" className="size-6" />
            </div>
            <div className="min-w-0 flex-1 compact:basis-35">
              <h3 className="max-w-92 font-sans text-[15px] font-semibold leading-[1.8] compact:leading-normal">
                {t('earning.zealy.join')}
              </h3>
            </div>
            <Button to="https://zealy.io/cw/hero/questboard" className="gap-2 compact:ml-auto">
              {t('earning.zealy.connect')}
              <Icon name="chevron_right" className="text-lg" />
            </Button>
          </div>
        )}
      </CardBox>

      <h2 className="mt-4 font-sans text-[14px] font-semibold text-section-title">
        {t('earning.hero.title')}
      </h2>
      <CardBox className="flex items-center gap-6 p-6 max-wide:gap-5 compact:gap-3 compact:p-4">
        <div className="flex size-13 shrink-0 items-center justify-center rounded-full bg-icon-background compact:size-10">
          <img src={heroLogo} alt="Hero Logo" className="size-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="max-w-92 font-sans text-[15px] font-semibold leading-[1.8] compact:leading-normal">
            {t('earning.hero.rewardPoints')}
          </h3>
          <p className="mt-1 max-w-92 text-[13px] leading-[1.8] text-body">
            {t('earning.hero.rewardPointsDescription')}
          </p>
        </div>
      </CardBox>
    </Stack>
  );
}

export default Earning;
